import { useCallback, useMemo, useState } from "react";

import { useNavigate, Outlet, useMatch, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AnimatePresence, motion } from "framer-motion";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import useIdleEffect from "../../lib/hooks/useIdleEffect";
import {
  useClientPlan,
  useDeliveryReceiver,
} from "../../lib/hooks/useQueryBoard";

import Title from "../../components/Title";
import Modal from "../../components/Modal";
import BackButton from "../../components/BackButton";

import { client } from "../../bit/board/hooks/useGraphQLClient";

import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import { MUTATION_CREATE_CUSTOM_DELIVERY_EVENT } from "../../config/graphql/mutation";
import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";
import OfflineModal from "../../components/Offline/OfflineModal";
import Curve from "../../components/Animations/PageTransitions/Curve";
import Bump from "../../components/Animations/Bump";
import Reveal from "../../components/Animations/Reveal";

const CustomDeliveryPage = () => {
  const match = useMatch({ path: "/custom-delivery/:action" });

  const action = match?.params?.action as "package" | "food";

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/custom-delivery\/${action}\/?$`).test(
    pathname,
  );

  const namespace = action === "food" ? "foodDelivery" : "packageDelivery";

  const [calling, setCalling] = useState<boolean>(false);

  const clientPlan = useClientPlan();

  const queryClient = useQueryClient();

  const { t } = useTranslation([namespace]);

  const navigate = useNavigate();

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const deliveryReceiver = useDeliveryReceiver();

  const {
    data,
    status: requestStatus,
    mutate: onCreateCustomDeliveryEvent,
  } = useMutation(
    async (input: {
      type: string;
      receiver: { fullName: string; email: string; phone?: string };
    }) => {
      const response = await client.request<
        { createCustomDeliveryEvent: IAccessEvent },
        {
          input: {
            type: string;
            receiver: { fullName: string; email: string; phone?: string };
          };
        }
      >(MUTATION_CREATE_CUSTOM_DELIVERY_EVENT, {
        input,
      });

      const accessScanId = response?.createCustomDeliveryEvent?.id;

      /**
       * Prefetch access event so we don't wait when on contact page
       */
      await queryClient
        .prefetchQuery([`access/event/${accessScanId}`], () =>
          client.request(QUERY_ACCESS_EVENT, { id: accessScanId }),
        )
        .catch(() => null);

      return response;
    },
  );

  const receiver = data?.createCustomDeliveryEvent?.customReceiver;

  const status = useDebounceStatus(requestStatus, 1500);

  const translationKey = useMemo(() => {
    const key = `${namespace}:status.${status}`;

    if (status !== "success") {
      return key;
    }

    let messageType = "no-email-sms";

    if (!!receiver?.phone) {
      messageType = "call-ping";
    } else {
      messageType = "email-sms";
    }

    return `${key}.${messageType}`;
  }, [namespace, status, receiver?.phone]);

  const onDismiss = useCallback(() => {
    const id = data?.createCustomDeliveryEvent?.id;

    const isFree = clientPlan && clientPlan === "free";

    const canContinue =
      !isFree && !!id && status === "success" && !!receiver?.phone;

    if (canContinue) {
      setCalling(true);

      return navigate(`/custom-delivery/${action}/contact/${id}/call`, {
        replace: true,
      });
    }

    return navigate("/", { replace: true });
  }, [status, data]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 6000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const onSubmit = useCallback(() => {
    if (!(deliveryReceiver?.email || deliveryReceiver?.fullName)) {
      return;
    }

    const input = {
      type: action === "package" ? "Package" : "Food",
      receiver: {
        fullName: deliveryReceiver.fullName,
        email: deliveryReceiver.email,
        ...(deliveryReceiver.phone && { phone: deliveryReceiver.phone }),
      },
    };

    onCreateCustomDeliveryEvent(input);
  }, [onCreateCustomDeliveryEvent, deliveryReceiver, action]);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1" className="delivery-title">
            <Trans i18nKey={`${namespace}:title`} />
          </Typography>
        </Title>
        <Reveal className="text-center">
          <Typography variant="h3" className="font-weight-regular mt-5">
            <Trans i18nKey={`${namespace}:subtitle1`} />
          </Typography>
          <Typography variant="h4" className="font-weight-regular mt-4 mb-5">
            <Trans i18nKey={`${namespace}:subtitle2`} />
          </Typography>
        </Reveal>
        <Bump disabled={!mainRoute}>
          <motion.div style={{ width: "54rem" }}>
            <Button
              variant="contained"
              className="mx-1 mt-5 py-5 w-100"
              onClick={onSubmit}
            >
              <Typography variant="h3" className="text-white">
                {t(`${namespace}:button.submit`)}
              </Typography>
            </Button>
          </motion.div>
        </Bump>

        <AnimatePresence mode="wait">
          {status !== "idle" && !calling && (
            <Modal
              status={status}
              // @ts-ignore
              title={<Trans i18nKey={`${translationKey}.title`} />}
              // @ts-ignore
              message={<Trans i18nKey={`${translationKey}.message`} />}
              onDismiss={onDismiss}
            />
          )}
        </AnimatePresence>

        <Outlet />
        <OfflineModal />
      </div>
    </Curve>
  );
};

export default CustomDeliveryPage;
